import { urlJoin } from '@/utils/url-join';

const LOGOUT_REDIRECT_PATH = '/';

export interface NamedSettledResult {
  name: string;
  result: PromiseSettledResult<unknown>;
}

export interface LogoutDeps {
  callLogoutApis: () => Promise<NamedSettledResult[]>;
  clearLegacyState: () => void;
  clearSessionStorage: () => void;
  invalidateQueryCache: () => void | Promise<void>;
  redirect: (url: string) => void;
  removeToken: () => void;
  reportError: (error: unknown, context: Record<string, string>) => void;
  resetAuthAtoms: () => void;
}

export interface LogoutConfig {
  beBaseUrl: string;
  origin: string;
}

export async function executeLogout(
  deps: LogoutDeps,
  config: LogoutConfig
): Promise<void> {
  // Phase 1: Clear client state synchronously to prevent UI flash
  deps.removeToken();
  deps.clearSessionStorage();
  deps.resetAuthAtoms();

  // Phase 2: Invalidate query cache (prevents stale refetches)
  await deps.invalidateQueryCache();

  // Phase 3: Backend logout calls (fire in parallel)
  let results: NamedSettledResult[] = [];

  try {
    results = await deps.callLogoutApis();
  } catch (error) {
    deps.reportError(error, {
      event_type: 'logout_api_unhandled_failure',
    });
  }

  for (const { name, result } of results) {
    if (result.status === 'rejected') {
      deps.reportError(result.reason, {
        operation: name,
        event_type: 'logout_api_failure',
      });
    }
  }

  // Phase 4: Clear legacy state after backends respond
  deps.clearLegacyState();

  // Phase 5: Redirect to .NET logout endpoint to clear remaining cookies
  try {
    const redirectUrl = urlJoin(config.origin, LOGOUT_REDIRECT_PATH);
    const logoutBaseUrl = urlJoin(config.beBaseUrl, '/logout');
    const logoutUrl = new URL(logoutBaseUrl);
    logoutUrl.searchParams.set('redirect', redirectUrl);
    deps.redirect(logoutUrl.toString());
  } catch (error) {
    deps.reportError(error, { event_type: 'logout_redirect_failure' });
    deps.redirect(urlJoin(config.beBaseUrl, '/logout'));
  }
}
